import { useEffect, useMemo, useRef, useState } from 'react';
import { organizationApi } from '@/lib/api/organizations';
import { brandsApi } from '@/lib/api/brands';
import { storeApi } from '@/lib/api/stores';
import type { Brand, Organization, PointAccount, Store } from '@/lib/api/types';
import { logger } from '@/utils/logger';

type BalanceSource = 'organization' | 'aggregate';
type StoreWithBrandReference = Store & {
  brand?: Pick<Brand, 'id' | 'slug' | 'name' | 'logo_url'> | null;
  brand_id?: number | null;
};

export interface PointsBrandSection {
  id: number;
  slug: string;
  name: string;
  logoUrl: string | null;
  pointsBalance: number;
  stores: Store[];
}

export interface PointsOrganizationSection {
  id: number;
  name: string;
  logoUrl: string | null;
  pointsBalance: number;
  balanceSource: BalanceSource;
  brands: PointsBrandSection[];
  standaloneStores: Store[];
}

interface UsePointsPortfolioResult {
  organizations: PointsOrganizationSection[];
  loading: boolean;
  error: string | null;
}

function nameToText(name: string | Record<string, string> | undefined, fallback: string): string {
  if (typeof name === 'string' && name.trim() !== '') {
    return name;
  }

  if (name && typeof name === 'object') {
    return name.ko ?? name['es-MX'] ?? name.en ?? Object.values(name)[0] ?? fallback;
  }

  return fallback;
}

function getStoreBrandId(store: Store): number | null {
  const storeWithBrand = store as StoreWithBrandReference;
  return storeWithBrand.brand?.id ?? storeWithBrand.brand_id ?? null;
}

function getStoreBrand(store: Store): StoreWithBrandReference['brand'] {
  return (store as StoreWithBrandReference).brand ?? null;
}

function accountByScope(accounts: PointAccount[], scopeType: PointAccount['scope_type']): Map<number, PointAccount> {
  return new Map(
    accounts
      .filter((account) => account.scope_type === scopeType)
      .map((account) => [account.scope_id, account]),
  );
}

function brandToSection(brand: Brand, brandAccount: PointAccount | undefined): PointsBrandSection {
  return {
    id: brand.id,
    slug: brand.slug,
    name: nameToText(brand.name, brandAccount?.scope_name ?? `Brand ${brand.id}`),
    logoUrl: brand.logo_url ?? null,
    pointsBalance: brandAccount?.balance ?? 0,
    stores: brand.stores ?? [],
  };
}

function mergeStores(base: Store[], incoming: Store[]): Store[] {
  const stores = new Map(base.map((store) => [store.id, store]));

  for (const store of incoming) {
    if (!stores.has(store.id)) {
      stores.set(store.id, store);
    }
  }

  return Array.from(stores.values());
}

function mergePointsBalance(existing: PointsOrganizationSection, incoming: PointsOrganizationSection): void {
  if (incoming.balanceSource === 'organization') {
    existing.pointsBalance = incoming.pointsBalance;
    existing.balanceSource = 'organization';
    return;
  }

  if (existing.balanceSource === 'organization') {
    return;
  }

  existing.pointsBalance += incoming.pointsBalance;
  existing.balanceSource = 'aggregate';
}

function mergeSections(sections: PointsOrganizationSection[]): PointsOrganizationSection[] {
  const organizations = new Map<number, PointsOrganizationSection>();

  for (const section of sections) {
    const existing = organizations.get(section.id);

    if (!existing) {
      organizations.set(section.id, {
        ...section,
        brands: section.brands.map((brand) => ({ ...brand, stores: [...brand.stores] })),
        standaloneStores: [...section.standaloneStores],
      });
      continue;
    }

    mergePointsBalance(existing, section);
    existing.logoUrl = existing.logoUrl ?? section.logoUrl;
    existing.name = existing.name || section.name;

    for (const brand of section.brands) {
      const existingBrand = existing.brands.find((candidate) => candidate.id === brand.id);

      if (!existingBrand) {
        existing.brands.push({ ...brand, stores: [...brand.stores] });
        continue;
      }

      existingBrand.logoUrl = existingBrand.logoUrl ?? brand.logoUrl;
      existingBrand.pointsBalance = Math.max(existingBrand.pointsBalance, brand.pointsBalance);
      existingBrand.stores = mergeStores(existingBrand.stores, brand.stores);
    }

    existing.standaloneStores = mergeStores(existing.standaloneStores, section.standaloneStores);
  }

  return Array.from(organizations.values());
}

function brandSectionsFromStores(
  stores: Store[],
  brandAccounts: Map<number, PointAccount>,
): PointsBrandSection[] {
  const sections = new Map<number, PointsBrandSection>();

  for (const store of stores) {
    const brandId = getStoreBrandId(store);
    if (brandId === null) {
      continue;
    }

    let section = sections.get(brandId);
    if (!section) {
      const brand = getStoreBrand(store);
      const brandAccount = brandAccounts.get(brandId);
      section = {
        id: brandId,
        slug: brand?.slug ?? `brand-${brandId}`,
        name: nameToText(brand?.name, brandAccount?.scope_name ?? `Brand ${brandId}`),
        logoUrl: brand?.logo_url ?? null,
        pointsBalance: brandAccount?.balance ?? 0,
        stores: [],
      };
      sections.set(brandId, section);
    }

    section.stores.push(store);
  }

  return Array.from(sections.values());
}

async function brandToSectionWithStores(
  brand: Brand,
  brandAccount: PointAccount | undefined,
  organizationId: number,
  organizationStores: Store[],
): Promise<PointsBrandSection> {
  try {
    const response = await brandsApi.getStoresByBrand(String(brand.id), organizationId);

    return {
      id: response.data.brand.id,
      slug: response.data.brand.slug,
      name: nameToText(response.data.brand.name, brandAccount?.scope_name ?? `Brand ${brand.id}`),
      logoUrl: response.data.brand.logo_url ?? brand.logo_url ?? null,
      pointsBalance: brandAccount?.balance ?? 0,
      stores: response.data.stores,
    };
  } catch (error) {
    logger.error('[usePointsPortfolio] 조직 브랜드 매장 조회 실패:', error, {
      organizationId,
      brandId: brand.id,
    });

    const storesFromOrganization = organizationStores.filter((store) => getStoreBrandId(store) === brand.id);
    return {
      ...brandToSection(brand, brandAccount),
      stores: storesFromOrganization.length > 0 ? storesFromOrganization : (brand.stores ?? []),
    };
  }
}

async function buildFromOrganizationAccount(
  account: PointAccount,
  brandAccounts: Map<number, PointAccount>,
): Promise<PointsOrganizationSection> {
  try {
    const orgParam = String(account.scope_id);
    const [organizationResult, brandsResult, storesResult] = await Promise.allSettled([
      organizationApi.getOrganization(orgParam),
      organizationApi.getBrandsByOrg(orgParam, { is_active: true }),
      organizationApi.getStoresByOrg(orgParam),
    ]);

    if (
      organizationResult.status === 'rejected'
      && brandsResult.status === 'rejected'
      && storesResult.status === 'rejected'
    ) {
      throw organizationResult.reason;
    }

    if (organizationResult.status === 'rejected') {
      logger.error('[usePointsPortfolio] 조직 상세 조회 실패:', organizationResult.reason, {
        scopeId: account.scope_id,
      });
    }
    if (brandsResult.status === 'rejected') {
      logger.error('[usePointsPortfolio] 조직 브랜드 목록 조회 실패:', brandsResult.reason, {
        scopeId: account.scope_id,
      });
    }
    if (storesResult.status === 'rejected') {
      logger.error('[usePointsPortfolio] 조직 매장 목록 조회 실패:', storesResult.reason, {
        scopeId: account.scope_id,
      });
    }

    const organization: Organization | null = organizationResult.status === 'fulfilled'
      ? organizationResult.value.data
      : null;
    const organizationSummary = brandsResult.status === 'fulfilled'
      ? brandsResult.value.data.organization
      : storesResult.status === 'fulfilled'
        ? storesResult.value.data.organization
        : null;
    const organizationId = organization?.id ?? organizationSummary?.id ?? account.scope_id;
    const organizationStores = storesResult.status === 'fulfilled' ? storesResult.value.data.stores : [];
    const organizationBrands = brandsResult.status === 'fulfilled' ? brandsResult.value.data.brands : [];
    const brands = await Promise.all(organizationBrands.map((brand) => (
      brandToSectionWithStores(brand, brandAccounts.get(brand.id), organizationId, organizationStores)
    )));
    const existingBrandIds = new Set(brands.map((brand) => brand.id));
    for (const brand of brandSectionsFromStores(organizationStores, brandAccounts)) {
      if (!existingBrandIds.has(brand.id)) {
        brands.push(brand);
      }
    }
    const brandedStoreIds = new Set(brands.flatMap((brand) => brand.stores.map((store) => store.id)));

    return {
      id: organizationId,
      name: organization?.name || organizationSummary?.name || account.scope_name,
      logoUrl: organization?.logo_url ?? organizationSummary?.logo_url ?? null,
      pointsBalance: account.balance,
      balanceSource: 'organization',
      brands,
      standaloneStores: organizationStores.filter((store) => (
        getStoreBrandId(store) === null && !brandedStoreIds.has(store.id)
      )),
    };
  } catch (error) {
    logger.error('[usePointsPortfolio] 조직 포인트 포트폴리오 조회 실패:', error, {
      scopeId: account.scope_id,
    });

    return {
      id: account.scope_id,
      name: account.scope_name || `Organization ${account.scope_id}`,
      logoUrl: null,
      pointsBalance: account.balance,
      balanceSource: 'organization',
      brands: [],
      standaloneStores: [],
    };
  }
}

async function buildFromBrandAccounts(
  brandAccounts: PointAccount[],
): Promise<PointsOrganizationSection[]> {
  const sections = new Map<number, PointsOrganizationSection>();

  await Promise.all(brandAccounts.map(async (account) => {
    try {
      const response = await brandsApi.getStoresByBrand(String(account.scope_id));
      const organization = response.data.brand.organization;
      const orgId = organization.id;
      const existing = sections.get(orgId);

      if (!existing) {
        sections.set(orgId, {
          id: orgId,
          name: nameToText(organization.name, ''),
          logoUrl: null,
          pointsBalance: 0,
          balanceSource: 'aggregate',
          brands: [],
          standaloneStores: [],
        });
      }

      const section = sections.get(orgId);
      if (!section) {
        return;
      }

      section.pointsBalance += account.balance;
      section.brands.push({
        id: response.data.brand.id,
        slug: response.data.brand.slug,
        name: nameToText(response.data.brand.name, account.scope_name),
        logoUrl: response.data.brand.logo_url ?? null,
        pointsBalance: account.balance,
        stores: response.data.stores,
      });
    } catch (error) {
      logger.error('[usePointsPortfolio] 브랜드 포인트 포트폴리오 조회 실패:', error, {
        scopeId: account.scope_id,
      });
    }
  }));

  const organizations = await Promise.all(Array.from(sections.values()).map(async (section) => {
    try {
      const response = await organizationApi.getOrganization(String(section.id));

      return {
        ...section,
        name: response.data.name || section.name,
        logoUrl: response.data.logo_url ?? null,
      };
    } catch {
      return section;
    }
  }));

  return organizations;
}

async function buildFromStoreAccounts(storeAccounts: PointAccount[]): Promise<PointsOrganizationSection[]> {
  const sections = new Map<number, PointsOrganizationSection>();

  await Promise.all(storeAccounts.map(async (account) => {
    try {
      const response = await storeApi.getStore(String(account.scope_id));
      const store = response.data;
      const organization = store.organization;
      const orgId = organization?.id ?? 0;
      const existing = sections.get(orgId);

      if (!existing) {
        sections.set(orgId, {
          id: orgId,
          name: organization?.name ?? '',
          logoUrl: null,
          pointsBalance: 0,
          balanceSource: 'aggregate',
          brands: [],
          standaloneStores: [],
        });
      }

      const section = sections.get(orgId);
      if (!section) {
        return;
      }

      section.pointsBalance += account.balance;

      if (store.brand) {
        let brand = section.brands.find((candidate) => candidate.id === store.brand?.id);
        if (!brand) {
          brand = {
            id: store.brand.id,
            slug: store.brand.slug,
            name: nameToText(store.brand.name, `Brand ${store.brand.id}`),
            logoUrl: store.brand.logo_url ?? null,
            pointsBalance: 0,
            stores: [],
          };
          section.brands.push(brand);
        }

        brand.pointsBalance += account.balance;
        brand.stores.push(store);
        return;
      }

      section.standaloneStores.push(store);
    } catch (error) {
      logger.error('[usePointsPortfolio] 매장 포인트 포트폴리오 조회 실패:', error, {
        scopeId: account.scope_id,
      });
    }
  }));

  return Promise.all(Array.from(sections.values()).map(async (section) => {
    if (section.id === 0) {
      return {
        ...section,
        name: section.name || 'Stores',
      };
    }

    try {
      const response = await organizationApi.getOrganization(String(section.id));

      return {
        ...section,
        name: response.data.name || section.name,
        logoUrl: response.data.logo_url ?? null,
      };
    } catch {
      return section;
    }
  }));
}

export function usePointsPortfolio(accounts: PointAccount[], enabled: boolean): UsePointsPortfolioResult {
  const accountsRef = useRef(accounts);
  const signature = useMemo(
    () => accounts.map((account) => (
      `${account.scope_type}:${account.scope_id}:${account.scope_name}:${account.balance}`
    )).join('|'),
    [accounts],
  );
  const [state, setState] = useState<UsePointsPortfolioResult>({
    organizations: [],
    loading: false,
    error: null,
  });
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    const currentAccounts = accountsRef.current;

    if (!enabled || currentAccounts.length === 0) {
      setState({ organizations: [], loading: false, error: null });
      setLoadedSignature(null);
      return;
    }

    let cancelled = false;
    const organizationAccounts = currentAccounts.filter((account) => account.scope_type === 'organization');
    const brandAccounts = currentAccounts.filter((account) => account.scope_type === 'brand');
    const storeAccounts = currentAccounts.filter((account) => account.scope_type === 'store');
    const brandAccountMap = accountByScope(currentAccounts, 'brand');

    setState((previous) => ({ ...previous, loading: true, error: null }));

    const request = Promise.all([
      Promise.all(organizationAccounts.map((account) => buildFromOrganizationAccount(account, brandAccountMap))),
      buildFromStoreAccounts(storeAccounts),
    ]).then(async ([organizationSections, storeSections]) => {
      const coveredBrandIds = new Set(organizationSections.flatMap((section) => (
        section.brands.map((brand) => brand.id)
      )));
      const remainingBrandAccounts = brandAccounts.filter((account) => !coveredBrandIds.has(account.scope_id));
      const brandSections = await buildFromBrandAccounts(remainingBrandAccounts);

      return mergeSections([
        ...organizationSections,
        ...brandSections,
        ...storeSections,
      ]);
    });

    request
      .then((organizations) => {
        if (!cancelled) {
          setState({ organizations, loading: false, error: null });
          setLoadedSignature(signature);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'POINTS_PORTFOLIO_LOAD_FAILED';
          setState({ organizations: [], loading: false, error: message });
          setLoadedSignature(signature);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, signature]);

  return {
    ...state,
    loading: state.loading || (enabled && accounts.length > 0 && loadedSignature !== signature && state.error === null),
  };
}
